import { render } from 'preact';
import { App } from './App';
import { appRouter } from './nav/appRouter';
import { connectAuthGuard } from './nav/guard';
import { openChat, openChatFromNotification } from './nav/actions';
import { appWorker } from './store/appWorker';
import { connectWake } from './store/wakeState';
import { connectColdStartRestore } from './store/restoreState';
import { globalStore } from './store/global';
import { appearanceRuntime } from './shell/appearanceRuntime';
import { swUpdate, swRegistration } from './store/swUpdate';
import { connectNotificationClicks } from './push/notificationClicks';
import { coldStartChatKey } from './push/coldStartOpen';
import { connectAppBadge } from './store/appBadge';
import { browserNotifyDeliveryDeps, connectNotifyDelivery } from './store/notifyDelivery';
import { notifyPopups } from './store/notifyPopup';
import { markOnce, PERF_MARKS } from './perf/marks';
import './styles/global.scss';

// 推送冷启动目标（21ak）：**必须在 router.init() 之前读** —— init 的 replaceState
// 不带 query，会把它清掉（刻意：刷新/返回不重放这次跳转）。详见 push/coldStartOpen.ts
const pushColdStartKey = coldStartChatKey(location.search);

// 先建路由（深链合成栈 + 写入 globalStore.nav），再渲染——首帧即正确页面
appRouter().init();
// 外观运行时应用器：建起 system 模式 matchMedia 监听（首帧主题 index.html 内联
// 脚本已落好，这里幂等找平 + 接管运行时切换，14.31c）
appearanceRuntime();
// 拉起 Worker 并异步读本地凭证（零 HTTP，不阻塞首帧；回包前 auth 是 unknown）
appWorker();
// 路由守卫：auth 回包后未登录 → 登录页，登录成功回原页（unknown 不动）
connectAuthGuard(globalStore(), appRouter());
// 回前台探活 + 隐藏时存滚动位置（13b；场景 I/J）
connectWake(globalStore(), appWorker());
// 推送冷启动：首页已经起来了，这里补上「再跳到对应会话」那一步（21ak）。
// **必须排在 connectColdStartRestore 之前**：push 完栈是两层，恢复上次会话的
// 「仅根路径单层栈」门禁自然不成立 → 它 no-op，用户点的这条通知赢
if (pushColdStartKey !== null) openChat(appRouter(), pushColdStartKey);
// 冷启动恢复上次会话与滚动位置（仅根路径 + 首个 auth 决议即登录态时生效）
connectColdStartRestore(globalStore(), appWorker(), (key) => openChat(appRouter(), key));

// 离线外壳：生产构建在 load 后注册 SW（15a）+ 接版本更新事件（15b：提示条 /
// standalone 安全时机自动刷新）。接线必须在这里而不是组件里：自动应用与 UI 无关
swUpdate();
// 点击推送通知 → 站内跳到对应会话（18b；SW 侧 focus 窗口后 postMessage 过来）
connectNotificationClicks((key) => openChatFromNotification(appRouter(), key));
// 应用角标跟随未读（18c1；非免打扰会话未读之和，零请求、失败静默）
connectAppBadge(globalStore(), navigator);
// 新消息 → 系统通知（18c2；事实源=已回流的会话行，零请求、未授权即静默）
// + 桌面前台改走窗口内提示条（18c2b；判定互斥，同一条消息只走其中一条通道）
connectNotifyDelivery(
  globalStore(),
  browserNotifyDeliveryDeps(
    swRegistration(),
    (key) => openChatFromNotification(appRouter(), key),
    () => globalStore().get().layout.isStandalone,
    notifyPopups(),
  ),
);

const root = document.getElementById('root');
if (!root) {
  throw new Error('#root not found');
}

render(<App />, root);
// 启动打点（20a）：render() 同步返回即首帧 DOM 已提交（docs/perf.md）
markOnce(PERF_MARKS.render);
